import { IcoBook16, IcoCode16, IcoCog16, IcoDatabase16, IcoLayer0116 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SuggestionListHandle } from '@/components/mention-suggestion';
import type { PromptMentionItem, PromptMentionKind } from './prompt-mention-items';

const ICON_BY_KIND: Record<PromptMentionKind, typeof IcoLayer0116> = {
  task: IcoLayer0116,
  input: IcoDatabase16,
  output: IcoCode16,
  tool: IcoCog16,
  skill: IcoBook16,
};

const SECTION_ORDER: PromptMentionKind[] = ['task', 'input', 'output', 'tool', 'skill'];

type PromptMentionListProps = {
  items: PromptMentionItem[];
  query: string;
  onSelect: (item: PromptMentionItem) => void;
};

const PromptMentionList = forwardRef<SuggestionListHandle, PromptMentionListProps>(
  ({ items, query, onSelect }, ref) => {
    const { t } = useTranslation('agents', { keyPrefix: 'agentBuilder.mention' });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const isKeyboardNav = useRef(false);

    const sections = (() => {
      let offset = 0;
      return SECTION_ORDER.flatMap(kind => {
        const sectionItems = items.filter(item => item.kind === kind);
        if (sectionItems.length === 0) return [];
        const section = { kind, items: sectionItems, offset };
        offset += sectionItems.length;
        return [section];
      });
    })();

    const flatList = sections.flatMap(s => s.items);

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useEffect(() => {
      if (!isKeyboardNav.current) return;
      isKeyboardNav.current = false;
      const el = containerRef.current?.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp') {
          isKeyboardNav.current = true;
          setSelectedIndex(prev => (prev <= 0 ? flatList.length - 1 : prev - 1));
          return true;
        }
        if (event.key === 'ArrowDown') {
          isKeyboardNav.current = true;
          setSelectedIndex(prev => (prev + 1) % Math.max(flatList.length, 1));
          return true;
        }
        // Tab completes like Enter — plain Tab only, so Shift+Tab keeps its focus-navigation meaning.
        if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) {
          const item = flatList[selectedIndex];
          if (item) {
            onSelect(item);
            return true;
          }
          return false;
        }
        return false;
      },
      // Items come straight from builder state — there's never an async search to wait on.
      setLoading: () => {},
    }));

    const sectionLabel = (kind: PromptMentionKind): string => {
      if (kind === 'task') return t($ => $.sections.tasks);
      if (kind === 'input') return t($ => $.sections.inputs);
      if (kind === 'output') return t($ => $.sections.outputs);
      if (kind === 'skill') return t($ => $.sections.skills);
      return t($ => $.sections.tools);
    };

    if (flatList.length === 0) {
      return (
        <div className="w-fit min-w-[260px] bg-primary border border-tertiary rounded-default shadow-md p-1">
          <div className="px-2 py-1.5 text-body-3 text-tertiary">
            {query.trim() ? t($ => $.noMatches, { query }) : t($ => $.empty)}
          </div>
        </div>
      );
    }

    const renderItem = (item: PromptMentionItem, index: number) => {
      const isSelected = index === selectedIndex;
      const Icon = ICON_BY_KIND[item.kind];
      return (
        <button
          key={item.key}
          type="button"
          data-index={index}
          className={cx(
            'flex items-center gap-2 w-full px-2 py-1.5 rounded-default text-left cursor-pointer',
            isSelected ? 'bg-(--dropdown-hover-bg)' : 'hover:bg-(--dropdown-hover-bg)',
          )}
          onClick={() => onSelect(item)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <span className="shrink-0 flex items-center text-tertiary">
            <Icon />
          </span>
          <span className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span
              className={cx(
                'text-body-3 text-primary break-words',
                (item.kind === 'output' || item.kind === 'input') && 'font-mono',
              )}
            >
              {item.label}
            </span>
            {item.sublabel && <span className="text-snippet-3 text-tertiary truncate">{item.sublabel}</span>}
          </span>
        </button>
      );
    };

    return (
      <div className="w-fit min-w-[260px] max-w-[calc(100vw-32px)] md:max-w-[480px] bg-primary border border-tertiary rounded-default shadow-md p-1 max-h-[320px] overflow-y-auto flex flex-col gap-3">
        <div ref={containerRef} className="flex flex-col gap-3">
          {sections.map(section => (
            <div key={section.kind} className="flex flex-col gap-0.5">
              <div className="px-2 pt-2 pb-1 text-tertiary text-caption-3">{sectionLabel(section.kind)}</div>
              {section.items.map((item, i) => renderItem(item, section.offset + i))}
            </div>
          ))}
        </div>
      </div>
    );
  },
);

export default PromptMentionList;
